import {ReplaySubject, takeUntil} from "rxjs";

const CANCEL = Symbol();
const NewToken = Symbol();

export type WithCancellation<T> = T & { cancellationToken?: Readonly<CancellationToken> };

export class TimeoutError extends Error {
  constructor(allottedTime: number) {
    super(`Operation Timed Out After ${allottedTime}ms`);
  }
}

export class OperationCancelledError extends Error {
  constructor() {
    super("Operation Cancelled");
  }
}

export function checkCancellation(token?: Readonly<CancellationToken>|{cancellationToken?: Readonly<CancellationToken>}): void {

  if (!token) {
    return;
  }

  if ('cancellationToken' in token) {
    token = token.cancellationToken;
  }

  if (token instanceof CancellationToken) {
    token.throwIfCancelled();
  }


}


export class CancellationToken {

  static #noneCache: CancellationToken | undefined;
  static get None() {
    return this.#noneCache ?? (this.#noneCache = new CancellationToken());
  }

  static readonly FromTimeout = (allottedTime: number) => {
    const source = new CancellationTokenSource();
    setTimeout(() => source.cancel(), allottedTime);
    return source.token;
  }

  #cancelled = false;
  #source?: CancellationTokenSource;
  private constructor(source?: CancellationTokenSource) {
    this.#cancelled = false;
    this.#source = source ?? new CancellationTokenSource();
  }

  static [NewToken](source?: CancellationTokenSource): CancellationToken {
    return new CancellationToken(source);
  }

  #asSubject: ReplaySubject<true>|undefined;

  asTakeUntil<T>() {
    return takeUntil<T>(this.asObservable());
  }

  asObservable() {
    if (this.#asSubject) {
      return this.#asSubject;
    }
    this.#asSubject = new ReplaySubject<true>(1);
    this.#source?.register(() => {
      this.#asSubject?.error(new OperationCancelledError());
    });
    return this.#asSubject;


  }

  #abortController: AbortController|undefined;

  asAbortSignal(): AbortSignal {
    if (this.#abortController) {
      return this.#abortController.signal;
    }
    this.#abortController = new AbortController();
    this.#source?.register(() => this.#abortController?.abort('Operation Cancelled'));
    return this.#abortController.signal;
  }

  throwIfCancelled() {
    if (this.isCancelled()) {
      throw new OperationCancelledError();
    }
  }

  isCancelled() {
    return this.#cancelled;
  }

  [CANCEL]() {
    this.#cancelled = true;
  }

}

export class CancellationTokenSource {

  readonly #token: CancellationToken;
  constructor() {
    this.#token = CancellationToken[NewToken](this);
  }

  cancel() {
    this.#token[CANCEL]();
    this.#registrations.forEach(fn => fn());
  }

  #registrations = new Set<() => void>();
  register(fn: () => void) {
    this.#registrations.add(fn);
  }

  get token(): CancellationToken {
    return this.#token;
  }

}

